/**
 * Mental Health Translations Module (French)
 * 
 * Contains: Mental Health Index, Crisis Statistics, RISE Projects, Regional Analysis
 * 
 * @module mentalHealth
 * @language fr
 */

export const mentalHealth = {
  mentalHealthOld: {
    title: "Indice de Santé Mentale",
    subtitle: "Analyse complète de la santé mentale et du bien-être",
    globalAverage: "Moyenne Mondiale",
    categoryDistribution: "Distribution des Catégories",
    assessedDimensions: "Dimensions Évaluées",
    methodology: "Méthodologie",
    top5Countries: "Top 5 Pays",
    regionalLeaders: "Leaders Régionaux",
    leadersByDimension: "Leaders par Dimension"
  },
  mentalHealthIndex: {
    title: "Indice de Santé Mentale",
    subtitle: "Analyse complète de la santé mentale et du bien-être",
    globalAverage: "Moyenne Mondiale",
    points: "points",
    criticalRisk: "risque critique",
    categoryDistribution: "Distribution des Catégories",
    assessedDimensions: "Dimensions Évaluées",
    methodology: "Méthodologie",
    top5Countries: "Top 5 Pays",
    regionalLeaders: "Leaders Régionaux",
    leadersByDimension: "Leaders par Dimension",
    overall: "Général",
    youthSuicide: "Suicide des Jeunes",
    serviceAccess: "Accès aux Services",
    prevalence: "Prévalence",
    globalMHIScore: "Score MHI Mondial",
    coverageNote: "Basé sur 114 pays JCI",
    lowestRisk: "risque le plus faible",
    highestRisk: "risque le plus élevé",
    mediumRisk: "risque moyen",
    regionalRankings: "Indice Régional",
    rank: "Rang",
    region: "Région",
    score: "Score",
    performance: "Performance",
    excellent: "Excellent",
    good: "Bon",
    moderate: "Modéré",
    concerning: "Préoccupant",
    critical: "Critique"
  },
  mentalHealthPage: {
    pageTitle: "Dimension Santé Mentale",
    weightInFII: "poids dans le FII",
    measuringWellbeing: "Mesurer le bien-être, la résilience et l'accès au soutien en santé mentale dans le monde.",
    quote: "Il n'y a pas de santé sans santé mentale.",
    fiiWeightDistribution: "Répartition des poids du FII",
    mentalHealth: "Santé Mentale",
    workforce: "Main-d'œuvre",
    entrepreneurship: "Entrepreneuriat",
    understandCrisis: "Comprendre la crise"
  },
  mentalHealth: {
    crisisTitle: "La Crise de Santé Mentale en Chiffres",
    crisisSubtitle: "Statistiques critiques affectant le bien-être mental mondial des jeunes",
    crisis: {
      inSeven: "1 sur 7",
      inSevenSuffix: "sur 7",
      inThree: "1 sur 3",
      inThreeSuffix: "sur 3",
      th: "ème",
      stat1Label: "jeunes dans le monde connaissent des troubles de santé mentale",
      stat2Label: "taux de prévalence de la dépression (doublé depuis 8,5% pré-pandémie)",
      stat3Label: "taux de prévalence de l'anxiété (doublé depuis 11% pré-pandémie)",
      stat4Label: "jeunes citent l'insécurité de l'emploi comme principale source d'anxiété",
      stat5Label: "jeunes africains signalent une anxiété extrême concernant les perspectives d'emploi",
      stat6Label: "se sentent moins bien que leurs parents en Amérique du Nord, Europe, MENA",
      stat7Label: "accès moyen mondial aux services de santé mentale (fourchette: 10-90%)",
      stat8Label: "cause de décès chez les 15-19 ans est le suicide",
      stat9Label: "des personnes atteintes de maladies mentales ne reçoivent pas de traitement"
    },
    severity: {
      crisis: "Crise",
      severeGap: "Écart Grave",
      concerning: "Préoccupant"
    },
    fiiDimensionTitle: "Dimension Santé Mentale du FII",
    fiiDimensionSubtitle: "Score Santé Mentale = 40% de l'Indice d'Impact Futur",
    regions: {
      europe: "Europe",
      americas: "Amérique",
      asiaPacific: "Asie et le Pacifique",
      africaMe: "Afrique/Moyen-Orient"
    },
    status: {
      good: "Bon",
      moderate: "Modéré",
      atRisk: "À Risque"
    },
    fii: {
      serviceAccess: "Accès aux Services",
      serviceAccessDef: "Pourcentage de 18-40 ans ayant accès aux services de santé mentale (soins primaires, spécialistes, services de crise)",
      countriesAssessed: "144 pays évalués",
      serviceAccessHighest: "Luxembourg, Danemark, Islande (80-90%)",
      serviceAccessLowest: "Somalie, Yémen, Tchad (<5%)",
      europeNote: "Infrastructure la plus solide",
      americasNote: "Varie selon les pays",
      asiaPacificNote: "Plus grande variation",
      africaMeNote: "Écarts critiques",
      serviceAccessFinding: "Accès ≠ qualité. Pénurie de prestataires: 13 mondialement pour 100k vs 300+ dans pays à revenu élevé (disparité 40x)",
      prevalence: "Prévalence des Troubles",
      prevalenceDef: "Pourcentage de jeunes connaissant dépression et/ou anxiété (symptômes cliniquement significatifs)",
      trend1: "Augmentation post-COVID: Doublé depuis base 2019",
      trend2: "Écart de genre: Femmes 1,5x-2x taux plus élevés",
      trend3: "Concentration d'âge: Plus élevé dans groupe 20-24",
      trend4: "Tendance croissante: Anxiété +52% augmentation (1990-2021)",
      prevalenceFinding: "La prévalence est relativement uniforme mondialement, mais l'accès ne l'est pas",
      suicideMortality: "Mortalité par Suicide",
      suicideDef: "Taux de suicide standardisé par âge pour 100.000 jeunes (18-40 ans)",
      suicideHighest: "Lituanie (25-30 pour 100k), Russie (20-25)",
      suicideMiddle: "Amérique du Nord (13-15), Moyenne européenne (10-12)",
      suicideLower: "Pays asiatiques (5-12, varie largement)",
      suicideLowest: "Pays africains (1,5-5 pour 100k, probablement sous-déclaré)",
      suicideCriticalFinding: "Suicide = 4ème cause de décès pour âges 15-29 (OMS)",
      suicideNote: "Pays à faible revenu probablement sous-déclarent en raison de l'accès aux soins, stigmatisation, lacunes de certification",
      subWeight: "Sous-poids",
      showDetails: "Afficher détails",
      globalAverage: "Moyenne mondiale",
      highest: "Plus élevé",
      lowest: "Plus bas",
      middle: "Moyen",
      lower: "Inférieur",
      regionalBreakdown: "Répartition Régionale",
      currentPrevalence: "Prévalence actuelle (2021-2025)",
      depression: "Dépression",
      anxiety: "Anxiété",
      both: "Les deux",
      keyTrends: "Tendances Clés",
      globalRange: "Fourchette mondiale",
      keyFinding: "Constat clé",
      criticalFinding: "Constat critique"
    },
    regionalScoresTitle: "Scores Régionaux de Santé Mentale",
    regionalScoresSubtitle: "Dimension Santé Mentale du FII: Performance Régionale",
    regional: {
      serviceAccess: "Accès aux Services",
      prevalence: "Prévalence",
      suicideRate: "Taux de Suicide",
      drivers: "Moteurs",
      opportunity: "Opportunité",
      note: "Note",
      europeDrivers: "Systèmes de santé universels, programmes anti-stigmatisation, intégration au travail",
      europeOpportunity: "Les programmes de pairs JCI peuvent accélérer les voies d'accès",
      europeNote: "Le succès européen est porté par les politiques — les modèles peuvent être adaptés mondialement",
      asiaPacificDrivers: "Variation économique: Corée et Japon élevés, Asie du Sud bas",
      asiaPacificOpportunity: "Solutions de santé mentale numérique (mobile-first)",
      asiaPacificNote: "La stigmatisation culturelle reste élevée — approches centrées sur les jeunes sont clés",
      americasDrivers: "Amérique du Nord forte, Amérique latine a de grands écarts",
      americasOpportunity: "RISE centré sur santé mentale peut être étendu ici",
      americasNote: "Taux de suicide élevés aux États-Unis malgré bon accès = traitement ≠ résultats",
      africaMeDrivers: "Pénurie d'infrastructure, pas prévalence (taux de troubles sont similaires mondialement)",
      africaMeOpportunity: "Modèles basés sur la communauté (JCI peut s'intégrer) qui contournent environnements cliniques",
      africaMeNote: "Plus grand potentiel pour partenariats JCI — modèle de soutien par les pairs est extensible",
      americasGap: "30-40% d'écart de couverture de service entre États-Unis/Canada et Amérique latine",
      africaMeGap: "Écart critique: seulement 10-30% de couverture de service vs 70%+ en Europe",
      gapLabel: "Écart"
    },
    gapAnalysis: {
      title: "Analyse d'Écart Régional du FII",
      description: "L'écart entre Europe et Afrique/MO représente la principale inégalité dans l'opportunité mondiale des jeunes",
      pointGap: "écart de points",
      comparison: "(Europe 75,28 vs Afrique/MO 53,12)"
    },
    legend: {
      good: "Bon (70+)",
      moderate: "Modéré (60-69)",
      atRisk: "À Risque (<60)"
    },
    riseTitle: "Initiatives de Santé Mentale RISE de JCI",
    riseSubtitle: "Initiatives de santé mentale JCI alignées avec écart Santé Mentale du FII",
    rise: {
      title: "Réponse JCI: Projets de Santé Mentale RISE",
      subtitle: "Échelle de Réponse de Santé Mentale JCI",
      mentalHealthProjects: "Projets de Santé Mentale",
      ofAllRiseInitiatives: "de toutes les initiatives JCI RISE",
      budgetMobilized: "Budget Mobilisé",
      millions: "millions",
      youthTouched: "Jeunes Touchés",
      programTypes: "Types de Programme",
      impact: "Impact",
      examples: "Exemples",
      awarenessName: "Campagnes de Sensibilisation à la Santé Mentale",
      awarenessDesc: "Événements d'éducation communautaire, campagnes sur réseaux sociaux, partenariats scolaires",
      awarenessImpact: "50.000+ atteints directement et indirectement",
      awarenessEx1: "Événements 'La Santé Mentale Compte'",
      awarenessEx2: "2M+ impressions sur réseaux sociaux",
      awarenessEx3: "Formation de soutien par les pairs dans écoles",
      peerSupportName: "Réseaux de Soutien par les Pairs",
      peerSupportDesc: "Formation de mentors pairs, groupes de soutien, volontaires de ligne d'urgence",
      peerSupportImpact: "5.000+ supporters pairs formés",
      peerSupportEx1: "Formation intervention de crise",
      peerSupportEx2: "Groupes de soutien pour dépression/anxiété",
      peerSupportEx3: "Cercles en ligne + en personne",
      crisisTrainingName: "Formation et Réponse à la Crise",
      crisisTrainingDesc: "Formation premiers intervenants, prévention du suicide, protocoles d'urgence",
      crisisTrainingImpact: "1.200+ intervenants de crise formés",
      crisisTrainingEx1: "Ateliers de prévention du suicide",
      crisisTrainingEx2: "Premiers secours en santé mentale",
      crisisTrainingEx3: "Personnel de ligne d'urgence",
      workplaceWellnessName: "Programmes de Bien-être au Travail",
      workplaceWellnessDesc: "Partenariats d'entreprise, formation santé mentale au travail, intégration EAP",
      workplaceWellnessImpact: "200+ entreprises atteintes",
      workplaceWellnessEx1: "Ateliers de bien-être pour employés",
      workplaceWellnessEx2: "Formation santé mentale pour managers",
      workplaceWellnessEx3: "Programmes de réduction du stress",
      regionalDistribution: "Distribution Régionale",
      projects: "projets",
      distributionNote: "Asie et le Pacifique mène en projets de santé mentale en raison de grande base de membres. Afrique et Moyen-Orient montre taux de croissance le plus élevé.",
      outcomesMeasured: "Résultats Mesurés",
      outcome1: "Sensibilisation accrue à la santé mentale",
      outcome2: "Réduction de la stigmatisation à chercher de l'aide",
      outcome3: "Connectés à des ressources professionnelles",
      outcome4: "A rapporté amélioration du bien-être",
      crisisInterventions: "interventions de crise réalisées"
    },
    actions: {
      title: "Actions Recommandées pour JCI",
      subtitle: "Priorités stratégiques pour combler l'écart de santé mentale",
      effort: "Effort",
      impactLabel: "Impact",
      action1Title: "Étendre Projets de Santé Mentale RISE",
      action1Desc: "Étendre projets RISE de santé mentale de 22% à 40% de toutes initiatives, en priorisant régions Afrique/MO",
      action1Effort: "12-18 mois",
      action1Impact: "2x portée de bénéficiaires",
      action2Title: "Former 10.000 Supporters Pairs",
      action2Desc: "Développer programme de formation de soutien par les pairs certifié et déployer dans toutes régions d'ici 2030",
      action2Effort: "24-36 mois",
      action2Impact: "Modèle durable",
      action3Title: "Expansion Bien-être au Travail",
      action3Desc: "Partenariat avec sponsors d'entreprise pour apporter formation santé mentale aux jeunes professionnels mondialement",
      action3Effort: "6-12 mois",
      action3Impact: "Partenariats d'entreprise",
      action4Title: "Plateforme Numérique de Santé Mentale",
      action4Desc: "Développer ressources de santé mentale mobile-first accessibles dans toutes régions JCI",
      action4Effort: "18-24 mois",
      action4Impact: "Accessibilité mondiale",
      action5Title: "Programme Ambassadeurs Santé Mentale Communautaire",
      action5Desc: "Former ambassadeurs de santé mentale locaux dans chaque organisation locale JCI pour fournir soutien communautaire continu",
      action5Effort: "12-24 mois",
      action5Impact: "Extensibilité de base"
    },
    dataSources: "Sources de Données",
    callToAction: {
      title: "La Santé Mentale est la Fondation",
      description: "Les données sont claires: la santé mentale est le principal différenciateur entre régions à haute et basse opportunité. Le modèle de soutien par les pairs de JCI est uniquement positionné pour adresser cet écart à grande échelle.",
      point1: "La santé mentale représente 40% du score FII — le facteur unique le plus important",
      point2: "L'écart de 22 points entre Europe et Afrique/MO est principalement dû à l'infrastructure de santé mentale",
      point3: "Les modèles de soutien par les pairs (non cliniques) sont idéaux pour les marchés émergents — extensibles et culturellement appropriés",
      actionTitle: "Actions Recommandées",
      action1: "Étendre les projets RISE de santé mentale en Afrique/MO (actuel: 22% de projets)",
      action2: "Former 10.000+ supporters de santé mentale pairs d'ici 2030",
      action3: "Intégrer composant santé mentale dans tous les programmes JCI",
      action4: "Partenariat avec OMS et ONGs de santé mentale pour développement de programme"
    },
    regionalAnalysis: {
      title: "Analyse Régionale FII: Plongée Profonde Santé Mentale",
      subtitle: "Répartition détaillée des indicateurs de santé mentale par région JCI",
      europe: {
        strength1: "Score MH général le plus élevé (78/100)",
        strength2: "Couverture universelle santé mentale dans pays nordiques",
        strength3: "Programmes santé mentale au travail répandus",
        strength4: "Faible stigmatisation en Europe Occidentale",
        challenge1: "Sud/Est Europe à la traîne (60-70 scores)",
        challenge2: "Isolement des jeunes augmentant post-pandémie",
        challenge3: "Longs temps d'attente pour soins spécialisés",
        challenge4: "Écarts d'accès rural dans certains pays",
        recommendation1: "Exploiter organisations locales haute performance comme modèles",
        recommendation2: "Se concentrer sur isolement et solitude des jeunes",
        recommendation3: "Étendre programmes santé mentale au travail",
        insight: "Le succès européen est porté par les politiques — les modèles peuvent être adaptés mondialement",
        expansion: "Se concentrer sur organisations locales Sud/Est Européennes"
      },
      asiaPacific: {
        strength1: "Adoption rapide santé mentale numérique",
        strength2: "Forts systèmes de soutien communautaire/familial",
        strength3: "Investissement gouvernemental en hausse (Japon, Corée, Australie)",
        strength4: "Population jeune réceptive aux nouvelles approches",
        challenge1: "Stigmatisation élevée dans nombreuses cultures (réduit recherche d'aide)",
        challenge2: "Pression académique/travail extrême (surtout Asie de l'Est)",
        challenge3: "Large variation: Australie (75) vs Asie du Sud (45-55)",
        challenge4: "Professionnels qualifiés limités hors grandes villes",
        recommendation1: "Solutions santé mentale digital-first",
        recommendation2: "Campagnes anti-stigmatisation centrées jeunes",
        recommendation3: "Réseaux soutien par pairs dans écoles/lieux de travail",
        insight: "La stigmatisation culturelle est la barrière — les approches centrées sur les jeunes sont clés",
        expansion: "Étendre santé mentale numérique dans la région"
      },
      americas: {
        limitedData: "Données complètes limitées en Amérique latine",
        strength1: "Amérique du Nord: Accès fort (70-80% couverture)",
        strength2: "Mouvement sensibilisation santé mentale croissant",
        strength3: "Expansion télésanté post-COVID",
        strength4: "Organisations défense santé mentale actives",
        challenge1: "Amérique latine: Grands écarts accès (30-50% couverture)",
        challenge2: "Taux suicide élevés aux États-Unis malgré bon accès",
        challenge3: "Barrières coût dans système santé États-Unis",
        challenge4: "Défis accès rural dans la région",
        recommendation1: "Se concentrer sur santé mentale Amérique latine (plus grand écart)",
        recommendation2: "Étendre programmes JIB + CYE dans nations à score plus bas",
        recommendation3: "Développer programmes soutien femmes entrepreneures",
        insight: "Seulement 8% de JCI mondial mais 15% du monde = POTENTIEL DE CROISSANCE",
        expansion: "Étendre de 90 projets RISE → 150+"
      },
      africaMe: {
        limitedData: "Données complètes limitées - nombreuses estimations",
        strength1: "Population jeune: Région la plus jeune (moyenne 29 ans)",
        strength2: "Aspiration entrepreneuriale: 52% jeunes veulent lancer entreprises",
        strength3: "Potentiel croissance: 10-20% croissance PIB annuelle (certaines régions)",
        strength4: "Saut numérique: Adoption mobile-first",
        challenge1: "Accès santé mentale: Seulement 10-30% couverture (UE est 70%)",
        challenge2: "Infrastructure santé: Pénurie critique (1-2 pour 100k)",
        challenge3: "Chômage des jeunes: 14-16% (mondial est 13%)",
        challenge4: "Crise NEET: 1 sur 3 jeunes pas dans école/travail",
        challenge5: "Écart financement: $1,7T mondialement, 30% Afrique/MO",
        challenge6: "Inégalité de genre: Femmes font face 2x barrières dans tous piliers",
        recommendation1: "Santé mentale: Modèle soutien par pairs (non clinique)",
        recommendation2: "Formation compétences: STEM + numérique (via JCI)",
        recommendation3: "Entrepreneuriat des jeunes: Microfinance + mentorat",
        recommendation4: "Autonomisation du genre: Double accent sur femmes",
        insight: "Écart FII: 22 points vs Europe = problème adressable. Projets RISE: 220 seulement (22%), besoin d'étendre 2-3x",
        expansion: "CRITIQUE: Expansion JCI ici = plus grand impact mondial. Plan 3-4 ans pour combler 50% écart FII"
      }
    }
  },
  globalTrends: {
    title: "Tendances Mondiales de Santé Mentale",
    subtitle: "La prévalence de la santé mentale a augmenté de plus de 25% depuis 2020",
    stat1Label: "Augmentation prévalence santé mentale des jeunes",
    stat1Period: "depuis 2019",
    stat2Label: "Augmentation taux suicide des jeunes",
    stat2Period: "depuis 2010",
    stat3Label: "Augmentation utilisation services santé mentale jeunes",
    stat3Period: "depuis 2018",
    globalTrendLabel: "Tendance Mondiale: Sensibilisation Santé Mentale",
    regionalLabel: "Répartition Régionale",
    trendDescription: "La pandémie COVID-19 a exacerbé les défis de santé mentale, affectant disproportionnellement les jeunes. Cependant, la sensibilisation augmente et la stigmatisation autour de la recherche d'aide diminue."
  },
  futureOriented: {
    title: "Perspectives Orientées Futur",
    subtitle: "Projections santé mentale et opportunités dans les régions JCI",
    trend1: {
      title: "Prévalence Croissante Mondialement",
      description: "Prévision que dépression et anxiété augmentent encore 15% d'ici 2030, faisant des programmes de prévention et intervention précoce une priorité."
    },
    trend2: {
      title: "Croissance Santé Mentale Numérique",
      description: "Prévision que téléthérapie et applications santé mentale croissent de 100% d'ici 2030, élargissant l'accès aux régions mal desservies."
    },
    trend3: {
      title: "Focus Santé Mentale au Travail",
      description: "Les employeurs augmentent leur investissement dans le bien-être des employés alors que la santé mentale devient un facteur clé de rétention des talents."
    },
    trend4: {
      title: "Soutien par Pairs Dirigé par Jeunes",
      description: "Les modèles de soutien par les pairs se révèlent extensibles et culturellement pertinents, surtout dans les régions à infrastructure clinique limitée."
    }
  }
} as const;
